import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, TextField, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, InputAdornment, FormControl, Select, MenuItem, InputLabel
} from '@mui/material';
import { UserContext } from '../../context/UserContext';
import API from '../../config/axiosConfig';
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import AddEditTestTube from './AddEditTestTube';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import hospitalIcon from '../../assets/protocols/hospital.svg';
import './TestTubes.scss';
import CommonLayout from '../../components/CommonLayout';
import FloatingAddButton from '../../components/FloatingAddButton';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import * as XLSX from 'xlsx';

// SVG Icons
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
  </svg>
);

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
  </svg>
);

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
  </svg>
);

/** --- Skeleton card (inline) --- **/
const TestTubeSkeletonRow = () => (
  <Skeleton>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#f5f5f5',
        borderRadius: 12,
        padding: '12px 16px',
        width: '100%',
        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
        marginBottom: 7,
        minHeight: 72,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
        <div style={{ width: 6, height: 48, borderRadius: 4, background: '#ddd' }} />
        <div style={{ flex: 1 }}>
          <Skeleton.Line width="55%" height="14px" />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Skeleton.Text width="80px"  height="20px" />
            <Skeleton.Text width="60px"  height="20px" />
            <Skeleton.Text width="100px" height="20px" />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <Skeleton.Block width="20px" height="20px" />
        <Skeleton.Block width="20px" height="20px" />
      </div>
    </div>
  </Skeleton>
);

const TestTubeManagement = () => {
  const [testTubes, setTestTubes] = useState([]);
  const [substitutesTubes, setSubstitutesTubes] = useState('');
  const [showSubstitutesTubes, setShowSubstitutesTubes] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [currentTestTube, setCurrentTestTube] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [selectedHospital, setSelectedHospital] = useState('');
  const [availableHospitals, setAvailableHospitals] = useState([]);
  const [hospitalsWithEditAccess, setHospitalsWithEditAccess] = useState([]);
  
  // User context for permissions
  const { user, hospitals, permittedApps } = useContext(UserContext);

  // Constants for configuration storage
  const APP_NAME = 'TEST_TUBES';
  const CONFIG_NAME = 'test-tubes';

  useEffect(() => {
    // Set up available hospitals based on user permissions
    if (hospitals && hospitals.length > 0 && user && permittedApps) {
      // Find the TEST_TUBES app and get hospitals where user has permissions
      const testTubesApp = permittedApps.find(app => app.key === APP_NAME);
      
      let editableHospitals = [];
      
      if (user.isSuperAdmin) {
        // Super admin has access to all hospitals
        editableHospitals = [...hospitals];
      } else if (testTubesApp) {
        // Get hospitals where user has admin permissions for TEST_TUBES
        const permittedHospitalIds = testTubesApp.admins
          .filter(admin => (admin.user._id === user._id))
          .map(admin => admin.hospital._id);
          
        editableHospitals = hospitals.filter(hospital =>
          permittedHospitalIds.includes(hospital._id)
        );
      }
      
      setHospitalsWithEditAccess(editableHospitals);
      
      let hospitalOptions = [...hospitals];
      
      // Add "apps" option for super admin
      if (user.isSuperAdmin) {
        hospitalOptions.push({
          _id: "apps",
          nameHebrew: "ברירת מחדל",
          nameEnglish: "apps"
        });
      }
      
      setAvailableHospitals(hospitalOptions);
      
      // Set default hospital selection
      if (editableHospitals.length === 1) {
        // If user has edit access to only one hospital, select it automatically
        setSelectedHospital(editableHospitals[0].nameEnglish);
      } else if (user.isSuperAdmin && hospitalOptions.length > 0) {
        // Default to "apps" for super admin with multiple options
        setSelectedHospital("apps");
      } else if (editableHospitals.length > 0) {
        // Default to first editable hospital
        setSelectedHospital(editableHospitals[0].nameEnglish);
      } else if (hospitalOptions.length > 0) {
        // Fallback to first hospital if no editable ones
        setSelectedHospital(hospitalOptions[0].nameEnglish);
      }
    }
  }, [hospitals, user, permittedApps]);

  // Filter test tubes based on search term
  const filteredTestTubes = testTubes.filter(tube => {
    if (!searchTerm.trim()) return true; // Show all if search is empty
    
    const lowercasedSearch = searchTerm.toLowerCase();
    
    // If keywords is an array, check if any of the keywords include the search term
    const keywordsMatch = Array.isArray(tube.keywords) && 
      tube.keywords.some(keyword => keyword.toLowerCase().includes(lowercasedSearch));
      
    return (
      (tube.mainTitle && tube.mainTitle.toLowerCase().includes(lowercasedSearch)) ||
      (tube.shortName && tube.shortName.toLowerCase().includes(lowercasedSearch)) ||
      keywordsMatch ||
      (tube.notes && tube.notes.toLowerCase().includes(lowercasedSearch))
    );
  });

  // Fetch test tubes from the server
  const fetchTestTubes = async (hospital) => {
    if (!hospital) return;
    
    try {
      setLoading(true);
      const response = await API.get(`/api/configs/${APP_NAME}/${CONFIG_NAME}/${hospital}`);
      if (response.data.success) {
        setTestTubes(response.data.data.tubes || []);
        setSubstitutesTubes(response.data.data.substitutesTubes || '');
      }
    } catch (error) {
      console.error('Error fetching test tubes:', error);
      showNotification('שגיאה בטעינת נתונים', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Save all test tubes to the server using the unified endpoint
  const saveTestTubes = async (updatedTestTubes, updatedSubstitutesTubes = substitutesTubes) => {
    if (!selectedHospital) return;
    
    const response = await API.post(`/api/configs/${APP_NAME}/${CONFIG_NAME}/${selectedHospital}`, {
      data: {
        "tubes": updatedTestTubes,
        "substitutesTubes": updatedSubstitutesTubes
      }
    });
    if (response.data.success) {
      setTestTubes(response.data.data.tubes || []);
      setSubstitutesTubes(response.data.data.substitutesTubes || '');        
    }
  };

  useEffect(() => {
    if (selectedHospital) {
      fetchTestTubes(selectedHospital);
    }
  }, [selectedHospital]);

  const handleHospitalChange = (event) => {
    const newHospital = event.target.value;
    setSelectedHospital(newHospital);
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, open: false }));
    }, 3000);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleClickOpen = (testTube, index) => {
    if (testTube) {
      setCurrentTestTube({...testTube});
      setEditMode(true);
      // Find the actual index in the full testTubes array
      const actualIndex = testTubes.findIndex(tube => tube === testTube);
      setEditIndex(actualIndex);
    } else {
      setCurrentTestTube({
        mainTitle: '',
        shortName: '',
        performingLaboratory: '',
        sampleSource: '',
        collectionTool: '',
        color: '',
        collectionInstructions: '',
        clarificationsAndExplanations: '',
        notes: '',
        keywords: [],
        hiddenKeywords: []
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentTestTube(null);
  };

  const handleSave = async (testTubeData) => {
    if (!selectedHospital) {
      showNotification('יש לבחור בית חולים תחילה', 'error');
      return;
    }
    
    setIsSubmitting(true);
    let updatedTestTubes;
    if (editMode) {
      updatedTestTubes = [...testTubes];
      updatedTestTubes[editIndex] = testTubeData;
    } else {
      updatedTestTubes = [...testTubes, testTubeData];
    }

    try {
      await saveTestTubes(updatedTestTubes);
      handleClose();
    } catch (error) {
      console.error('Error saving test tube:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteConfirm = (e, testTube) => {
    e.stopPropagation();
    // Find the actual index in the full testTubes array
    const actualIndex = testTubes.findIndex(tube => tube === testTube);
    setDeleteIndex(actualIndex);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (deleteIndex !== null) {
      setIsSubmitting(true);
      try {
        const updatedTestTubes = [...testTubes];
        updatedTestTubes.splice(deleteIndex, 1);
        await saveTestTubes(updatedTestTubes);
        setDeleteConfirmOpen(false);
        setDeleteIndex(null);
      } catch (error) {
        console.error('Error deleting test tube:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const canManageTestTubes = () => {
    if (!user || !selectedHospital) return false;
    if (user.isSuperAdmin) return true;

    const selectedHospitalObj = hospitals.find(h => h.nameEnglish === selectedHospital);
    if (!selectedHospitalObj) return false;

    return hospitalsWithEditAccess.some(h => h._id === selectedHospitalObj._id);
  };

  // Create app link with the correct hospital parameter
  const getAppLink = () => {
    return `https://my.mashlom.me/main/${selectedHospital}/test-tubes`;
  };

  // Export test tubes to Excel
  const handleExportToExcel = () => {
    if (testTubes.length === 0) {
      showNotification('אין נתונים לייצוא', 'error');
      return;
    }

    // Prepare data for export
    const exportData = testTubes.map((tube) => ({
      'כותרת ראשית': tube.mainTitle || '',
      'שם מקוצר': tube.shortName || '',
      'מעבדה מבצעת': tube.performingLaboratory || '',
      'מקור דגימה': tube.sampleSource || '',
      'כלי איסוף': tube.collectionTool || '',
      'צבע': tube.color || '',
      'הוראות איסוף': tube.collectionInstructions || '',
      'הבהרות והסברים': tube.clarificationsAndExplanations || '',
      'הערות': tube.notes || '',
      'מילות מפתח': Array.isArray(tube.keywords) ? tube.keywords.join(', ') : '',
      'מילות מפתח נסתרות': Array.isArray(tube.hiddenKeywords) ? tube.hiddenKeywords.join(', ') : ''
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const colWidths = [
      { wch: 25 }, // כותרת ראשית
      { wch: 15 }, // שם מקוצר
      { wch: 20 }, // מעבדה מבצעת
      { wch: 15 }, // מקור דגימה
      { wch: 15 }, // כלי איסוף
      { wch: 10 }, // צבע
      { wch: 30 }, // הוראות איסוף
      { wch: 30 }, // הבהרות והסברים
      { wch: 30 }, // הערות
      { wch: 25 }, // מילות מפתח
      { wch: 25 }  // מילות מפתח נסתרות
    ];
    ws['!cols'] = colWidths;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'מבחנות מעבדה');

    // Generate filename with hospital name and date
    const hospitalName = availableHospitals.find(h => h.nameEnglish === selectedHospital)?.nameHebrew || selectedHospital;
    const date = new Date().toISOString().split('T')[0];
    const filename = `מבחנות_מעבדה_${hospitalName}_${date}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
    showNotification('הקובץ יוצא בהצלחה', 'success');
  };

  return (
    <div className="test-tubes-container">
    <CommonLayout>       
      {/* Header */}
      <div className="test-tube-header-container">
        <Typography variant="h4" component="h1" className="header-title">
          ניהול מבחנות מעבדה
        </Typography>
      </div>

      {/* Action Buttons Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginBottom: '24px',
        gap: '16px'
      }}>
        <Button
          variant="contained"
          onClick={handleExportToExcel}
          disabled={loading || testTubes.length === 0}
          startIcon={<DownloadIcon />}
          sx={{
            backgroundColor: '#103C6E',
            color: 'white',
            borderRadius: '8px',
            padding: '8px 20px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            '& .MuiButton-startIcon': {
              marginLeft: '12px'
            },
            '&:hover': {
              backgroundColor: '#0d2f54'
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666'
            }
          }}
        >
          יצוא
        </Button>

        <a
          href={getAppLink()}
          target="_blank"
          rel="noopener noreferrer"
          className="app-link"
        >
          לאפליקציה <ArrowLeftIcon />
        </a>
      </div>
      
      {/* Hospital Selector - Only show if user has edit permissions for multiple hospitals */}
      {(hospitalsWithEditAccess.length > 1 || (user?.isSuperAdmin && hospitals.length > 0)) && (
        <div style={{ marginBottom: '24px' }}>
          <DropdownWithIcon
            icon={hospitalIcon}
            placeholder="בית חולים"
            value={selectedHospital}
            onChange={handleHospitalChange}
          >
            {user?.isSuperAdmin ? (
              availableHospitals.map((hospital) => (
                <option key={hospital._id} value={hospital.nameEnglish}>
                  {hospital.nameHebrew}
                </option>
              ))
            ) : (
              hospitalsWithEditAccess.map((hospital) => (
                <option key={hospital._id} value={hospital.nameEnglish}>
                  {hospital.nameHebrew}
                </option>
              ))
            )}
          </DropdownWithIcon>
        </div>
      )}
      
      {/* Substitute Tubes Section - Simple Collapsible */}
      {canManageTestTubes() && (
        <div className="substitute-tubes-section" style={{ marginBottom: '20px' }}>
          <div
            onClick={() => setShowSubstitutesTubes(!showSubstitutesTubes)}
            style={{
              cursor: 'pointer',
              color: '#103C6E',
              display: 'flex',
              alignItems: 'center',
              marginBottom: '10px'
            }}
          >
            <span style={{ marginLeft: '5px', fontSize: '18px' }}>
              {showSubstitutesTubes ? '−' : '+'}
            </span>
            <Typography
              component="span"
              style={{
                fontWeight: 500,
                textDecoration: 'underline'
              }}
            >
              מבחנות תחליפיות
            </Typography>
          </div>
          
          {showSubstitutesTubes && (
            <div>
              <TextField
                fullWidth
                multiline
                rows={5}
                value={substitutesTubes}
                onChange={(e) => setSubstitutesTubes(e.target.value)}
                placeholder="הזן מידע על מבחנות תחליפיות כאן..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px'
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  saveTestTubes(testTubes, substitutesTubes);
                  setShowSubstitutesTubes(false);
                }}
                style={{
                  backgroundColor: '#103C6E',
                  color: 'white',
                  marginTop: '10px'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'שומר...' : 'שמור מבחנות תחליפיות'}
              </Button>
            </div>
          )}
        </div>
      )}
      
      {/* Search Box */}
      <div className="search-box">
        <TextField
          fullWidth
          placeholder="חיפוש לפי כותרת, שם מקוצר, מילות מפתח או הערות..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            style: { borderRadius: '8px' }
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            }
          }}
        />
      </div>

      {/* Test Tubes List Skeleton */}
      {loading ? (
        <div className="test-tubes-list">
          {Array.from({ length: 6 }).map((_, i) => <TestTubeSkeletonRow key={i} />)}
        </div>
      ) : (
        <div className="test-tubes-list">
          {testTubes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography>לא נמצאו מבחנות. הוסף מבחנה חדשה כדי להתחיל.</Typography>
            </div>
          ) : filteredTestTubes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography>לא נמצאו תוצאות התואמות לחיפוש שלך.</Typography>
            </div>
          ) : (
            filteredTestTubes.map((testTube, index) => (
              <div 
                key={index}
                className="test-tube-item"
                onClick={() => canManageTestTubes() && handleClickOpen(testTube, index)}
                style={{ position: 'relative', cursor: canManageTestTubes() ? 'pointer' : 'default' }}
              >
                {/* Color indicator on the left side */}
                {testTube.color && (
                  <div 
                    className="color-indicator" 
                    style={{ backgroundColor: testTube.color }}
                  />
                )}
                
                <div className="test-tube-info">
                  <div className="test-tube-title">{testTube.mainTitle}</div>
                  
                  {testTube.keywords && testTube.keywords.length > 0 && (
                    <div className="test-tube-keywords">
                      {testTube.keywords.map((kw, i) => (
                        <span key={i} className="test-tube-keyword">{kw}</span>
                      ))}
                    </div>
                  )}
                </div>
                
                {canManageTestTubes() && (
                  <div 
                    className="test-tube-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="action-icon" onClick={(e) => {
                      e.stopPropagation();
                      handleClickOpen(testTube, index);
                    }}>
                      <EditIcon />
                    </div>
                    <div className="action-icon" onClick={(e) => openDeleteConfirm(e, testTube)}>
                      <DeleteIcon />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Floating Add Button - Only show if user has edit permissions for the selected hospital */}
      {canManageTestTubes() && selectedHospital && (
        <FloatingAddButton
          onClick={() => handleClickOpen(null)}
          entityName="מבחנה"
          dataLoaded={!loading}
        />
      )}

      {/* Add/Edit Test Tube Dialog */}
      {open && (
        <AddEditTestTube
          open={open}
          onClose={handleClose}
          testTube={currentTestTube}
          editMode={editMode}
          onSave={handleSave}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="האם אתה בטוח שברצונך למחוק את המבחנה?"
        isDeleting={isSubmitting}
      />

      {/* Notification */}
      {notification.open && (
        <div style={{ 
          position: 'fixed', 
          bottom: '20px', 
          left: '20px', 
          zIndex: 2000,
          backgroundColor: notification.severity === 'error' ? '#f44336' : '#4caf50',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>{notification.message}</div>
            <button 
              onClick={handleCloseNotification}
              style={{ 
                marginRight: '10px', 
                background: 'none', 
                border: 'none', 
                color: 'white',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </CommonLayout>
    </div>
    
  );
  
};

export default TestTubeManagement;
